import OpportunityRadar from './components/OpportunityRadar';
import { useState, useEffect } from 'react';
import Header from './components/Header';
import CubeScene from './components/CubeScene';
import CubeSelector from './components/CubeSelector';
import QuickAccess from './components/QuickAccess';
import FloatingAI from './components/FloatingAI';
import SearchModule from './components/SearchModule';
import FunnelsModule from './components/FunnelsModule';
import BrittanyCorner from './components/BrittanyCorner';
import InvestorCenter from './components/InvestorCenter';
import FamilyVault from './components/FamilyVault';
import VaultModule from './components/VaultModule';
import DashboardModule from './components/DashboardModule';
import EducationCenter from './components/EducationCenter';
import { useAuth } from './contexts/AuthContext';
import { canAccessModule, getAccessDeniedMessage, isPurchasableLock } from './utils/moduleAccess';
import type { ModuleId, CubeType } from './types';
import './styles/global.css';

function App() {
  const [mode, setMode] = useState<'standby' | 'active'>('standby');
  const [activeModule, setActiveModule] = useState<ModuleId | null>(null);
  const [activeCube, setActiveCube] = useState<CubeType>('command');
  const [accessDenied, setAccessDenied] = useState<string | null>(null);
  const [lockedModule, setLockedModule] = useState<boolean>(false);
  const [entitledModules, setEntitledModules] = useState<string[] | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === 'CUSTOMER') {
      fetch(`/api/entitlements/me`, { credentials: 'include' })
        .then(res => (res.ok ? res.json() : { allowed_modules: [] }))
        .then(data => setEntitledModules(data.allowed_modules || []))
        .catch(() => setEntitledModules([]));
    } else {
      setEntitledModules(null);
    }
  }, [user]);

  const handleFaceClick = (id: ModuleId) => {
    // Check role-based access + paid entitlements (backend remains authoritative)
    if (user && !canAccessModule(id, user.role, entitledModules)) {
      setAccessDenied(getAccessDeniedMessage(id, user.role, entitledModules));
      setLockedModule(isPurchasableLock(id, user.role, entitledModules));
      setActiveModule(id);
      setMode('active');
      return;
    }
    
    setAccessDenied(null);
    setLockedModule(false);
    setActiveModule(id);
    setMode('active');
  };

  const handleBack = () => {
    setMode('standby');
    setActiveModule(null);
    setAccessDenied(null);
  };

  const getSubtitle = (id: ModuleId) => {
    switch(id) {
      // Command Cube
      case 'dashboard': return 'Mission Control • Priorities • Status';
      case 'vault': return 'Assets • Documents • Storage';
      case 'ai': return 'Assistant • Automation • Workflows';
      case 'control': return 'Settings • Permissions • Config';
      case 'investor': return 'Capital Access • Deal Flow • Analytics';
      case 'funnels': return 'Revenue Generation • Execution Layer';
      // Legacy Cube
      case 'brittany': return 'Creative • Strategy • Development';
      case 'legacy-projects': return 'Past Projects • Archive • History';
      case 'family-vault': return 'Family Assets • Legacy Planning';
      case 'education-center': return 'Learning • Courses • Resources';
      case 'creator-studio': return 'Content Creation • Publishing';
      case 'future-planning': return 'Vision • Goals • Strategy';
      // Growth Cube
      case 'search': return 'Sovereign Intelligence • NWG Command Center';
      case 'affiliate-tools': return 'Partner Programs • Commission Tracking';
      case 'product-builder': return 'Product Development • Launch System';
      case 'analytics': return 'Performance Metrics • Data Intelligence';
      case 'campaigns': return 'Marketing Campaigns • Automation';
      case 'marketing-systems': return 'Traffic • Conversions • Scaling';
      default: return '';
    }
  };

  return (
    <div style={styles.app}>
      <div className={`global-watermark ${mode === 'active' ? 'dimmed' : ''}`}>
        <img src="/brand/nwg-logo-web.png" alt="NextWealth Global — Show Us the Problems. We Build the Solutions." />
      </div>
      
      <Header 
        mode={mode}
        onReturnHome={handleBack}
      />

      {mode === 'standby' && (
        <main style={styles.standby}>
          <CubeSelector 
            activeCube={activeCube}
            onCubeChange={setActiveCube}
          />
          <CubeScene
            onSelect={handleFaceClick}
            mode={mode}
            cubeType={activeCube}
          />
          <QuickAccess
            currentCube={activeCube}
            onModuleSelect={handleFaceClick}
            onCubeChange={setActiveCube}
          />
        </main>
      )}

      {mode === 'active' && activeModule && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'black',
          color: '#FFD700',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 9999,
        }}>
          
          {/* Header */}
          <div style={{
            padding: '20px',
            borderBottom: '1px solid gold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: 'clamp(14px, 4vw, 18px)', fontWeight: 'bold' }}>
                ACTIVE PANEL: {activeModule.toUpperCase().replace(/-/g, ' ')}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.7 }}>
                {getSubtitle(activeModule)}
              </div>
            </div>

            <button onClick={handleBack} style={{
              background: 'black',
              color: 'gold',
              border: '1px solid gold',
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: '12px',
              flexShrink: 0,
              marginLeft: '12px'
            }}>
              RETURN TO CUBE
            </button>
          </div>

          {/* Content Area — must scroll vertically when content exceeds viewport */}
          <div style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {accessDenied ? (
              <div style={{ textAlign: 'center', padding: '40px', margin: 'auto', fontSize: '24px' }} data-testid="module-locked-panel">
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '20px', color: lockedModule ? '#FFD700' : '#ff4444' }}>
                  {lockedModule ? 'MODULE LOCKED' : 'ACCESS RESTRICTED'}
                </div>
                <div style={{ fontSize: '16px', opacity: 0.7, marginBottom: '20px' }}>
                  {accessDenied}
                </div>
                {lockedModule ? (
                  <a
                    href="/checkout"
                    data-testid="locked-module-checkout-link"
                    style={{
                      display: 'inline-block',
                      marginTop: '20px',
                      padding: '12px 24px',
                      background: '#FFD700',
                      color: 'black',
                      fontWeight: 'bold',
                      letterSpacing: '1px',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      fontSize: '14px',
                    }}
                  >
                    UNLOCK VIA CHECKOUT
                  </a>
                ) : (
                  <div style={{ fontSize: '14px', opacity: 0.5, marginTop: '40px' }}>
                    Contact your administrator to request access.
                  </div>
                )}
              </div>
            ) : activeModule === 'search' ? (
              <SearchModule onNavigate={(moduleId) => {
                handleFaceClick(moduleId);
              }} />
            ) : activeModule === 'funnels' ? (
              <FunnelsModule />
            ) : activeModule === 'brittany' ? (
              <BrittanyCorner onBack={handleBack} />
            ) : activeModule === 'investor' ? (
              <InvestorCenter onBack={handleBack} />
            ) : activeModule === 'vault' ? (
              <VaultModule />
            ) : activeModule === 'dashboard' ? (
              <DashboardModule viewMode="grid" />
            ) : activeModule === 'family-vault' ? (
              <FamilyVault onBack={handleBack} />
            ) : activeModule === 'opportunity-radar' ? (
              <OpportunityRadar />
            ) : activeModule === 'education-center' ? (
              <EducationCenter />
            ) : (
              <div style={{ textAlign: 'center', margin: 'auto', padding: '40px' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '20px' }}>
                  {activeModule.toUpperCase().replace(/-/g, ' ')} MODULE
                </div>
                <div style={{ marginTop: '40px', fontSize: '16px', opacity: 0.5 }}>
                  {activeCube.toUpperCase()} CUBE
                </div>
              </div>
            )}
          </div>

        </div>
      )}

      <FloatingAI 
        activeModule={activeModule}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  app: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: 'var(--bg)'
  },
  standby: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

export default App;
